import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useClub } from '../../app/club';
import { useRepo, useServices } from '../../app/services';
import {
  useAccessEvents,
  useAccessRules,
  useBuildings,
  useClubMutation,
  useCredentials,
  useDoors,
  useGroupMembers,
  useGroups,
  useLocations,
  useLocks,
  useLockHardening,
  usePersons,
  useSyncJobs,
} from '../../data/hooks';
import {
  Badge,
  Battery,
  Button,
  CREDENTIAL_STATUS,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Loading,
  PageHeader,
  Panel,
  SyncBadge,
  useToast,
  type Tone,
} from '../../components/ui';
import { doorSyncStatuses, type DoorSyncStatus } from '../../lib/sync';
import { batteryInfo, batteryMeasuredAt } from '../../lib/battery';
import { fmtAge, fmtAgo, fmtDate, fmtWeekdayDateTime, MODEL_HINT, MODEL_LABEL } from '../../lib/format';
import { weekdaysLabel, windowLabel } from '../../lib/weekdays';
import { can, canAny } from '../../lib/roles';
import { autoLockText } from '../../lib/auditText';
import { useQuery } from '@tanstack/react-query';
import { EVENT_METHOD_LABEL } from './LogbookPage';
import { CRED_TYPE } from './PersonDetailPage';
import type { Building, Door, Location, Lock, LockConfigState, LockHardening, LockModel } from '../../lib/types';
import { canUnlink, DOOR_KIND_LABEL, unlinkedLocks } from '../../lib/doors';
import { errorText } from '../../lib/errors';
import { BuildingDialog, DoorDialog, LinkDoorDialog, LinkLockDialog, LocationDialog } from '../../components/DoorManagement';
import { formatAddress } from '../../lib/address';

/** Titels van de checklistpunten (zelfde ids als apps/mobile/src/poc/hardening.ts). */
const HARDENING_LABEL: Record<string, string> = {
  default_code_refused: 'Fabriekscode 123456# werkt niet meer',
  random_finger_refused: 'Een vreemde vinger wordt geweigerd',
  reset_card_envelope: 'Resetkaart verzegeld in envelop',
  auto_lock_on: 'Auto-lock staat aan',
  passage_mode_off: 'Passagemodus staat uit',
  clock_synced: 'Klok van het slot gelijkgezet',
  emergency_keys: 'Noodsleutels vastgelegd',
  escape_inside_free: 'Binnenknop draait vrij (nooduitgang)',
};
const HARDENING_ANSWER: Record<string, { label: string; tone: 'green' | 'red' | 'yellow' }> = {
  yes: { label: 'In orde', tone: 'green' },
  no: { label: 'Niet in orde', tone: 'red' },
  na: { label: 'Deur staat het toe', tone: 'yellow' },
};

function HardeningBadge({ h }: { h: LockHardening | undefined }) {
  if (!h) return <Badge tone="yellow">Niet ingevuld</Badge>;
  if (!h.completed) return <Badge tone="red">Niet afgerond</Badge>;
  return <Badge tone="green">Opgeleverd</Badge>;
}

function syncState(s: DoorSyncStatus) {
  if (!s.lock) return 'none' as const;
  if (s.oldestRevokeAt) return 'revoke' as const;
  if (s.pendingCount > 0) return 'pending' as const;
  if (s.failedCount > 0) return 'failed' as const;
  return 'synced' as const;
}

type DoorEdit = { door: Door | null; buildingId?: string };
type BuildingEdit = { building: Building | null; locationId?: string };

export function DoorsPage() {
  const { orgId, role, roles, readOnly } = useClub();
  const canManage = canAny('door.manage', roles) && !readOnly;
  const [editLocation, setEditLocation] = useState<Location | 'new' | null>(null);
  const [editBuilding, setEditBuilding] = useState<BuildingEdit | null>(null);
  const [editDoor, setEditDoor] = useState<DoorEdit | null>(null);
  const [linkLock, setLinkLock] = useState<Lock | null>(null);
  const [params, setParams] = useSearchParams();
  const locations = useLocations(orgId);
  const buildings = useBuildings(orgId);
  const doors = useDoors(orgId);
  const locks = useLocks(orgId);
  const jobs = useSyncJobs(orgId);
  // Installatiegegevens (checklist, ingeleerde middelen) zijn voor beheerders; group_manager ziet ze niet (RLS).
  const showInstall = role !== 'group_manager';
  // Bewust niet blokkerend: zonder checklistdata blijft de deurenlijst gewoon werken.
  const hardening = useLockHardening(orgId);
  const hardeningByLock = new Map((hardening.data ?? []).map((h) => [h.lock_id, h]));

  const statuses = useMemo(
    () => (doors.data && locks.data && jobs.data ? doorSyncStatuses(doors.data, locks.data, jobs.data) : []),
    [doors.data, locks.data, jobs.data],
  );
  const qs = [locations, buildings, doors, locks, jobs];
  if (qs.some((q) => q.isLoading)) return <Loading />;
  const err = qs.find((q) => q.error)?.error;
  if (err) return <ErrorState error={err} />;

  const selectedId = params.get('deur');
  const selected = statuses.find((s) => s.door.id === selectedId) ?? null;
  const open = (id: string | null) => {
    const p = new URLSearchParams(params);
    if (id) p.set('deur', id);
    else p.delete('deur');
    setParams(p, { replace: true });
  };

  const loose = unlinkedLocks(locks.data!, doors.data!);
  const hasLocations = locations.data!.length > 0;
  const allBuildings = buildings.data!;
  const activeBuildings = allBuildings.filter((b) => !b.archived_at);
  const buildingName = new Map(allBuildings.map((b) => [b.id, b.name]));
  const activeDoorsIn = (buildingId: string) => doors.data!.filter((d) => d.building_id === buildingId && !d.archived_at).length;

  const doorTable = (rows: DoorSyncStatus[]) => (
    <div className="table-wrap">
      <table className="data stack-mobile doors-table">
        {/* Vaste kolombreedtes: de tabellen per gebouw staan dan netjes onder elkaar. */}
        <colgroup>
          <col style={{ width: showInstall ? '19%' : '23%' }} />
          <col style={{ width: '13%' }} />
          <col style={{ width: '17%' }} />
          <col style={{ width: '12%' }} />
          <col style={{ width: showInstall ? '26%' : '35%' }} />
          {showInstall && <col style={{ width: '13%' }} />}
        </colgroup>
        <thead>
          <tr>
            <th scope="col">Deur / ruimte</th>
            <th scope="col">Slot</th>
            <th scope="col">Batterij</th>
            <th scope="col">Laatste sync</th>
            <th scope="col">Status</th>
            {showInstall && <th scope="col">Checklist</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.door.id}>
              <td className="cell-main">
                <button
                  className="btn btn-ghost"
                  style={{ padding: 0, minHeight: 0, fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 3 }}
                  onClick={() => open(s.door.id)}
                >
                  {s.door.name}
                </button>
                <span className="cell-sub">
                  {DOOR_KIND_LABEL[s.door.kind]}
                  {s.door.escape_route ? ' · nooduitgang' : ''}
                </span>
              </td>
              <td data-label="Slot">
                {s.lock ? (
                  <span className="model-tag" title={MODEL_HINT[s.lock.model]}>
                    {MODEL_LABEL[s.lock.model]}
                  </span>
                ) : (
                  <span className="muted">
                    Geen slot
                    {s.door.expected_model && <span className="cell-sub">komt: {MODEL_LABEL[s.door.expected_model]}</span>}
                  </span>
                )}
                {s.lock && <span className="cell-sub">{s.lock.serial}</span>}
              </td>
              <td data-label="Batterij">
                {s.lock ? <Battery pct={s.lock.battery_pct} measuredAt={batteryMeasuredAt(s.lock)} detail /> : <span className="muted">—</span>}
              </td>
              <td data-label="Laatste sync" className="num">
                {s.lock ? fmtAgo(s.lock.last_sync_at) : '—'}
              </td>
              <td>
                {s.lock && s.lock.status !== 'installed' ? (
                  <Badge tone="yellow">Wacht op installatie</Badge>
                ) : (
                  <SyncBadge state={syncState(s)} count={s.pendingCount} age={s.oldestPendingAt ? fmtAge(s.oldestPendingAt) : undefined} />
                )}
                {s.pendingCount > 0 && <span className="cell-sub">oudste {fmtAge(s.oldestPendingAt)}</span>}
              </td>
              {showInstall && (
                <td data-label="Checklist">
                  {s.lock && hardening.data ? (
                    <>
                      <HardeningBadge h={hardeningByLock.get(s.lock.id)} />
                      {hardeningByLock.get(s.lock.id)?.completed_at && (
                        <span className="cell-sub">{fmtDate(hardeningByLock.get(s.lock.id)!.completed_at)}</span>
                      )}
                    </>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="stack-lg">
      <PageHeader
        title="Deuren"
        intro="Per locatie en gebouw: welk slot er op elke deur zit, hoe vol de batterij is en of de laatste wijzigingen al op het slot staan."
        actions={
          canManage && (
            <>
              <Button onClick={() => setEditLocation('new')}>Locatie toevoegen</Button>
              {hasLocations && <Button onClick={() => setEditBuilding({ building: null })}>Gebouw toevoegen</Button>}
              {activeBuildings.length > 0 && (
                <Button variant="primary" onClick={() => setEditDoor({ door: null })}>
                  Deur toevoegen
                </Button>
              )}
            </>
          )
        }
      />
      {!hasLocations && (
        <Panel>
          <EmptyState
            title="Nog geen locaties en deuren"
            action={canManage ? <Button variant="primary" onClick={() => setEditLocation('new')}>Locatie toevoegen</Button> : undefined}
          >
            {canManage
              ? 'Begin met een locatie, bijvoorbeeld het sportpark. Daarna voeg je de gebouwen toe (kantine, kleedkamers, ballenhok), zet je per gebouw de deuren klaar en koppel je per deur het juiste slot.'
              : 'Antreja of je installateur zet de locaties, gebouwen en deuren klaar en koppelt de sloten.'}
          </EmptyState>
        </Panel>
      )}
      {showInstall && loose.length > 0 && (
        <Panel
          title="Sloten zonder deur"
          description={`${loose.length} ${loose.length === 1 ? 'slot is' : 'sloten zijn'} aan de club toegewezen maar hangen nog aan geen deur. Koppel elk slot aan de deur waar het op komt.`}
        >
          <ul className="tile-list">
            {loose.map((l) => (
              <li key={l.id}>
                <span>
                  <span className="primary">{l.serial ?? 'Zonder serienummer'}</span>
                  <span className="cell-sub">
                    <span className="model-tag" title={MODEL_HINT[l.model]}>
                      {MODEL_LABEL[l.model]}
                    </span>{' '}
                    {MODEL_HINT[l.model]}
                  </span>
                </span>
                {canManage && hasLocations && (
                  <Button size="sm" onClick={() => setLinkLock(l)}>
                    Aan deur koppelen
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </Panel>
      )}
      {locations.data!.map((loc) => {
        const locRows = statuses.filter((s) => s.door.location_id === loc.id);
        const locBuildings = activeBuildings.filter((b) => b.location_id === loc.id);
        if (!locRows.length && !canManage) return null;
        // Deuren in een (gearchiveerd of onbekend) gebouw dat hier niet als actief gebouw staat.
        const orphanRows = locRows.filter((s) => !s.door.building_id || !locBuildings.some((b) => b.id === s.door.building_id));
        const address = formatAddress(loc);
        return (
          <section className="panel" key={loc.id} aria-labelledby={`loc-${loc.id}`}>
            <div className="loc-head">
              <h2 id={`loc-${loc.id}`} style={{ fontSize: '1.35rem' }}>
                {loc.name}
                {address && <span className="loc-address">{address}</span>}
              </h2>
              <span className="row" style={{ gap: 8 }}>
                <span className="muted small">{loc.gateway_device_id ? 'Met gateway' : 'Zonder gateway: sync via de app'}</span>
                {canManage && (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => setEditLocation(loc)}>
                      Bewerken
                    </Button>
                    <Button size="sm" onClick={() => setEditBuilding({ building: null, locationId: loc.id })}>
                      Gebouw toevoegen
                    </Button>
                  </>
                )}
              </span>
            </div>
            {locBuildings.length === 0 && orphanRows.length === 0 && (
              <p className="tile-empty">
                {canManage
                  ? 'Nog geen gebouwen op deze locatie. Voeg een gebouw toe, bijvoorbeeld de kantine of een ballenhok; daarna zet je er de deuren onder.'
                  : 'Nog geen gebouwen op deze locatie.'}
              </p>
            )}
            {locBuildings.map((b) => {
              const rows = locRows.filter((s) => s.door.building_id === b.id);
              if (!rows.length && !canManage) return null;
              return (
                <div key={b.id} role="group" aria-labelledby={`bld-${b.id}`}>
                  <div className="bld-head">
                    <h3 id={`bld-${b.id}`}>
                      {b.name}
                      {b.notes && <span className="loc-address">{b.notes}</span>}
                    </h3>
                    {canManage && (
                      <span className="row" style={{ gap: 8 }}>
                        <Button size="sm" variant="ghost" onClick={() => setEditBuilding({ building: b })}>
                          Bewerken
                        </Button>
                        <Button size="sm" onClick={() => setEditDoor({ door: null, buildingId: b.id })}>
                          Deur toevoegen
                        </Button>
                      </span>
                    )}
                  </div>
                  {rows.length === 0 ? <p className="tile-empty">Nog geen deuren in dit gebouw.</p> : doorTable(rows)}
                </div>
              );
            })}
            {orphanRows.length > 0 && (
              <div role="group" aria-label="Zonder gebouw">
                <div className="bld-head">
                  <h3>Zonder gebouw</h3>
                  <span className="muted small">Kies bij Bewerken van de deur een gebouw.</span>
                </div>
                {doorTable(orphanRows)}
              </div>
            )}
          </section>
        );
      })}
      {selected && (
        <DoorDetail
          status={selected}
          buildingName={selected.door.building_id ? buildingName.get(selected.door.building_id) : undefined}
          hardening={selected.lock ? hardeningByLock.get(selected.lock.id) : undefined}
          showInstall={showInstall}
          hardeningKnown={!!hardening.data}
          locks={locks.data!}
          doors={doors.data!}
          onEdit={() => setEditDoor({ door: selected.door })}
          onClose={() => open(null)}
        />
      )}
      {editLocation && (
        <LocationDialog
          location={editLocation === 'new' ? null : editLocation}
          hasDoors={editLocation !== 'new' && doors.data!.some((d) => d.location_id === editLocation.id)}
          onClose={() => setEditLocation(null)}
        />
      )}
      {editBuilding && (
        <BuildingDialog
          building={editBuilding.building}
          locations={locations.data!}
          defaultLocationId={editBuilding.locationId}
          activeDoors={editBuilding.building ? activeDoorsIn(editBuilding.building.id) : 0}
          onClose={() => setEditBuilding(null)}
        />
      )}
      {editDoor && (
        <DoorDialog
          door={editDoor.door}
          locations={locations.data!}
          buildings={allBuildings}
          defaultBuildingId={editDoor.buildingId}
          onClose={() => setEditDoor(null)}
        />
      )}
      {linkLock && (
        <LinkDoorDialog lock={linkLock} doors={doors.data!} locations={locations.data!} buildings={allBuildings} onClose={() => setLinkLock(null)} />
      )}
    </div>
  );
}

function DoorDetail({
  status,
  buildingName,
  hardening,
  showInstall,
  hardeningKnown,
  locks,
  doors,
  onEdit,
  onClose,
}: {
  status: DoorSyncStatus;
  buildingName?: string;
  hardening: LockHardening | undefined;
  showInstall: boolean;
  /** false zolang de checklistdata niet geladen is (of de query faalde): dan de sectie niet tonen. */
  hardeningKnown: boolean;
  locks: Lock[];
  doors: Door[];
  onEdit: () => void;
  onClose: () => void;
}) {
  const { orgId, role, roles, readOnly } = useClub();
  const repo = useRepo();
  const { mode } = useServices();
  const isInstaller = role === 'installer';
  const toast = useToast();
  const rules = useAccessRules(orgId);
  const groups = useGroups(orgId);
  const members = useGroupMembers(orgId);
  const persons = usePersons(orgId);
  const credentials = useCredentials(orgId);
  const since = useMemo(() => new Date(Date.now() - 7 * 86400_000).toISOString(), []);
  const events = useAccessEvents(orgId, { door_id: status.door.id, since });
  const sync = useClubMutation(orgId, (lockId: string) => repo.simulateBleSync!(lockId));
  const passage = useClubMutation(orgId, (on: boolean) => repo.setPassageMode(status.door.id, on));
  const replaced = useClubMutation(orgId, (lockId: string) => repo.markBatteryReplaced(lockId));
  const unlink = useClubMutation(orgId, () => repo.unlinkLockFromDoor(status.door.id));
  const archive = useClubMutation(orgId, () => repo.archiveDoor(status.door.id));
  const [linking, setLinking] = useState(false);
  const [confirm, setConfirm] = useState<'unlink' | 'archive' | null>(null);
  const [manageError, setManageError] = useState<string | null>(null);
  const canManage = canAny('door.manage', roles) && !readOnly;

  const doorRules = (rules.data ?? []).filter((r) => r.door_id === status.door.id);
  const personName = new Map((persons.data ?? []).map((p) => [p.id, p.full_name]));
  const { door, lock } = status;
  const lockCreds = lock
    ? (credentials.data ?? []).filter((c) => c.lock_id === lock.id && c.status !== 'revoked' && c.status !== 'expired')
    : [];

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={buildingName ? `${buildingName} · ${door.name}` : door.name}
      wide
      description={lock ? `${MODEL_LABEL[lock.model]} (${MODEL_HINT[lock.model]}), serienummer ${lock.serial ?? 'onbekend'}` : 'Er hangt nog geen slot aan deze deur.'}
    >
      <div className="stack-lg">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          {lock && lock.status !== 'installed' ? (
            <Badge tone="yellow">Wacht op installatie</Badge>
          ) : (
            <SyncBadge state={syncState(status)} count={status.pendingCount} />
          )}
          <span className="muted small">Laatste sync {fmtAgo(lock?.last_sync_at)}</span>
        </div>
        {canManage && (
          <section className="stack" aria-labelledby="door-lock-head">
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <h3 id="door-lock-head">Slot op deze deur</h3>
              <Button size="sm" variant="ghost" onClick={onEdit}>
                Deur bewerken
              </Button>
            </div>
            {lock ? (
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span>
                  <strong>{lock.serial ?? 'Zonder serienummer'}</strong>{' '}
                  <span className="model-tag" title={MODEL_HINT[lock.model]}>
                    {MODEL_LABEL[lock.model]}
                  </span>
                  <span className="cell-sub">
                    {lock.status === 'installed'
                      ? 'Geïnstalleerd en verbonden met de Antreja-app. Resetten naar de fabrieksinstellingen doe je in de app, bij het slot: Profiel › Sloten van de club.'
                      : 'Gekoppeld. De installateur plaatst het slot en verbindt het met de Antreja-app.'}
                  </span>
                </span>
                {canUnlink(lock, role === 'platform_admin') ? (
                  <Button size="sm" onClick={() => setConfirm('unlink')}>
                    Ontkoppelen
                  </Button>
                ) : (
                  <span className="muted small">Ontkoppelen van een geïnstalleerd slot doet Antreja.</span>
                )}
              </div>
            ) : (
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="muted">
                  Er hangt nog geen slot aan deze deur
                  {door.expected_model ? `; hier komt een ${MODEL_LABEL[door.expected_model]}` : ''}.
                </span>
                <span className="row" style={{ gap: 8 }}>
                  {canAny('door.archive', roles) && !readOnly && (
                    <Button size="sm" variant="ghost" onClick={() => setConfirm('archive')}>
                      Deur archiveren
                    </Button>
                  )}
                  <Button size="sm" variant="primary" onClick={() => setLinking(true)}>
                    Slot koppelen
                  </Button>
                </span>
              </div>
            )}
            {confirm && (
              <div className="alert alert-warn" role="alertdialog" aria-label="Bevestigen">
                <p style={{ margin: '0 0 10px' }}>
                  {confirm === 'unlink'
                    ? lock?.status === 'installed'
                      ? 'Het slot gaat van deze deur af. Iedereen verliest de toegang via dit slot; het slot moet opnieuw gekoppeld worden.'
                      : 'Het slot gaat van deze deur af en komt terug bij de sloten zonder deur.'
                    : 'De deur verdwijnt uit de lijst en uit de toegangsregels. De historie in het logboek blijft bewaard.'}
                </p>
                <div className="row">
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={unlink.isPending || archive.isPending}
                    onClick={async () => {
                      setManageError(null);
                      try {
                        if (confirm === 'unlink') {
                          await unlink.mutateAsync(undefined);
                          toast('Slot ontkoppeld');
                        } else {
                          await archive.mutateAsync(undefined);
                          toast(`${door.name} gearchiveerd`);
                          onClose();
                        }
                        setConfirm(null);
                      } catch (err) {
                        setManageError(errorText(err, 'Dat lukte niet'));
                      }
                    }}
                  >
                    {confirm === 'unlink' ? 'Ja, ontkoppelen' : 'Ja, archiveren'}
                  </Button>
                  <Button size="sm" onClick={() => setConfirm(null)}>
                    Annuleren
                  </Button>
                </div>
              </div>
            )}
            {manageError && (
              <div className="alert alert-danger" role="alert">
                {manageError}
              </div>
            )}
          </section>
        )}
        {linking && <LinkLockDialog door={door} locks={locks} doors={doors} onClose={() => setLinking(false)} />}

        {status.pendingCount > 0 && (
          <div className="alert alert-warn">
            {status.pendingCount} wijziging{status.pendingCount === 1 ? '' : 'en'} opgeslagen, maar nog niet op het slot. Ze gaan mee zodra iemand met de
            Antreja-app bij deze deur is.
          </div>
        )}
        {mode === 'demo' && lock && status.pendingCount > 0 && (
          <Button
            onClick={async () => {
              const n = await sync.mutateAsync(lock.id);
              toast(`${n} wijziging${n === 1 ? '' : 'en'} op het slot gezet (demo)`);
            }}
            disabled={sync.isPending}
          >
            Simuleer app bij de deur
          </Button>
        )}

        {lock && (
          <section className="stack">
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <h3>Batterij</h3>
              <Battery pct={lock.battery_pct} />
            </div>
            <p className="muted small">
              {(() => {
                const b = batteryInfo(lock.battery_pct, batteryMeasuredAt(lock));
                if (b.level === 'unknown') return 'Nog niet gemeten. De stand komt binnen zodra iemand met de Antreja-app bij de deur is.';
                return `${b.measured[0]!.toUpperCase()}${b.measured.slice(1)}, toen iemand met de app bij de deur was. ${
                  b.level === 'red'
                    ? 'Vervang de batterijen zo snel mogelijk.'
                    : b.level === 'orange'
                      ? 'De batterij raakt op; zorg dat er nieuwe batterijen klaarliggen.'
                      : 'De batterij is in orde.'
                }`;
              })()}
            </p>
            {canAny('lock.battery_replaced', roles) && !readOnly && lock.battery_pct !== null && lock.battery_pct < 100 && (
              <div>
                <Button
                  size="sm"
                  disabled={replaced.isPending}
                  onClick={async () => {
                    await replaced.mutateAsync(lock.id);
                    toast('Batterijen vervangen: stand op 100% gezet');
                  }}
                >
                  Batterijen vervangen
                </Button>
                <span className="cell-sub">Klik hierop nadat je nieuwe batterijen hebt geplaatst.</span>
              </div>
            )}
          </section>
        )}

        {!isInstaller && (
        <section className="stack">
          <h3>Wie heeft toegang</h3>
          {doorRules.length === 0 ? (
            <p className="muted">Geen enkele groep heeft toegang tot deze deur.</p>
          ) : (
            <table className="data">
              <thead>
                <tr>
                  <th scope="col">Groep</th>
                  <th scope="col">Dagen</th>
                  <th scope="col">Tijd</th>
                  <th scope="col">Leden</th>
                </tr>
              </thead>
              <tbody>
                {doorRules.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <Link to={`/c/${orgId}/groepen/${r.group_id}`}>{groups.data?.find((g) => g.id === r.group_id)?.name}</Link>
                    </td>
                    <td>{weekdaysLabel(r.weekdays)}</td>
                    <td>{windowLabel(r)}</td>
                    <td className="num">{(members.data ?? []).filter((m) => m.group_id === r.group_id).length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
        )}

        {showInstall && lock && (
          <section className="stack">
            <h3>Passen, vingers en pincodes op dit slot</h3>
            <p className="muted small">Ingeleerd door de installateur met de Antreja-app. Intrekken doe je bij de persoon.</p>
            {credentials.isLoading ? (
              <Loading />
            ) : lockCreds.length === 0 ? (
              <p className="muted">Nog niets ingeleerd op dit slot.</p>
            ) : (
              <table className="data">
                <thead>
                  <tr>
                    <th scope="col">Wie</th>
                    <th scope="col">Middel</th>
                    <th scope="col">Ingeleerd</th>
                    <th scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {lockCreds.map((c) => (
                    <tr key={c.id}>
                      <td>
                        {c.person_id ? (
                          <Link to={`/c/${orgId}/personen/${c.person_id}`}>{personName.get(c.person_id) ?? c.person_label ?? 'Onbekend'}</Link>
                        ) : (
                          <>
                            {c.person_label ?? 'Onbekend'}
                            <span className="cell-sub">Niet gekoppeld aan een persoon</span>
                          </>
                        )}
                      </td>
                      <td>
                        {CRED_TYPE[c.type]}
                        {c.label && c.label !== CRED_TYPE[c.type] ? `: ${c.label}` : ''}
                      </td>
                      <td className="num">{fmtDate(c.created_at)}</td>
                      <td>
                        <Badge tone={CREDENTIAL_STATUS[c.status].tone}>{CREDENTIAL_STATUS[c.status].label}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        )}

        {showInstall && hardeningKnown && lock && (
          <section className="stack">
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <h3>Opleverchecklist</h3>
              <span className="row">
                <HardeningBadge h={hardening} />
                {hardening?.completed_at && <span className="muted small">{fmtDate(hardening.completed_at)}</span>}
              </span>
            </div>
            {!hardening ? (
              <p className="muted">De installateur heeft de checklist voor dit slot nog niet ingevuld.</p>
            ) : (
              <ul className="tile-list">
                {hardening.items.map((i) => (
                  <li key={i.itemId}>
                    <span>
                      <span className="primary">{HARDENING_LABEL[i.itemId] ?? i.itemId}</span>
                      {i.text && <span className="cell-sub">{i.text}</span>}
                    </span>
                    <Badge tone={HARDENING_ANSWER[i.answer]?.tone ?? 'yellow'}>{HARDENING_ANSWER[i.answer]?.label ?? i.answer}</Badge>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        {can('door.auto_lock', role) && !readOnly && <AutoLockSection door={door} orgId={orgId} model={lock?.model ?? null} />}

        {door.passage_mode_allowed && can('door.passage_mode', role) && !readOnly && (
          <section className="stack">
            <h3>Passagemodus</h3>
            <p className="muted small">De deur blijft open tot je passagemodus weer uitzet. Gaat via de volgende sync naar het slot.</p>
            <div className="row">
              <Button size="sm" onClick={async () => { await passage.mutateAsync(true); toast('Passagemodus aan: wacht op sync'); }}>
                Zet aan
              </Button>
              <Button size="sm" onClick={async () => { await passage.mutateAsync(false); toast('Passagemodus uit: wacht op sync'); }}>
                Zet uit
              </Button>
            </div>
          </section>
        )}

        <section className="stack">
          <h3>Afgelopen 7 dagen</h3>
          <p className="muted small">Events komen binnen bij de volgende app-verbinding met het slot.</p>
          {events.isLoading ? (
            <Loading />
          ) : (events.data ?? []).length === 0 ? (
            <p className="muted">Geen events ontvangen.</p>
          ) : (
            <table className="data">
              <thead>
                <tr>
                  <th scope="col">Tijd</th>
                  <th scope="col">Wie</th>
                  <th scope="col">Methode</th>
                  <th scope="col">Resultaat</th>
                </tr>
              </thead>
              <tbody>
                {events.data!.slice(0, 25).map((e) => (
                  <tr key={e.id}>
                    <td className="num">{fmtWeekdayDateTime(e.occurred_at)}</td>
                    <td>{e.person_id ? personName.get(e.person_id) ?? 'Onbekend' : <span className="muted">Code / gast</span>}</td>
                    <td>{EVENT_METHOD_LABEL[e.method]}</td>
                    <td>{e.result === 'granted' ? <Badge tone="green">Open</Badge> : <Badge tone="red">Geweigerd</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
      <div className="dialog-actions">
        <Button onClick={onClose}>Sluiten</Button>
      </div>
    </Dialog>
  );
}

const CONFIG_STATE: Partial<Record<LockConfigState, { tone: Tone; text: string }>> = {
  pending: { tone: 'yellow', text: 'Wacht op het slot: wordt ingesteld zodra iemand met de app bij het slot is.' },
  reported: { tone: 'yellow', text: 'Ingesteld via de app; bevestiging volgt.' },
  applied: { tone: 'green', text: 'Staat op het slot.' },
  failed: { tone: 'red', text: 'Instellen op het slot lukte niet. Probeer het opnieuw of neem contact op met Antreja.' },
};

/** Automatisch op slot per deur: standaard aan na 5 seconden; club mag de tijd aanpassen (3 s – 30 min) of uitzetten. */
function AutoLockSection({ door, orgId, model }: { door: Door; orgId: string; model: LockModel | null }) {
  const repo = useRepo();
  const toast = useToast();
  const enabled0 = door.auto_lock_enabled ?? true;
  const secs0 = door.auto_lock_seconds ?? 5;
  const [enabled, setEnabled] = useState(enabled0);
  const [unit, setUnit] = useState<'s' | 'min'>(secs0 >= 60 && secs0 % 60 === 0 ? 'min' : 's');
  const [amount, setAmount] = useState(String(secs0 >= 60 && secs0 % 60 === 0 ? secs0 / 60 : secs0));
  const state = useQuery({ queryKey: ['club', orgId, 'lockConfig', door.id], queryFn: () => repo.getDoorLockConfigState(door.id), enabled: !!door.lock_id });
  const save = useClubMutation(orgId, (v: { enabled: boolean; seconds: number }) => repo.setDoorAutoLock(door.id, v.enabled, v.seconds));

  const n = Number(amount.replace(',', '.'));
  const seconds = Math.round(unit === 'min' ? n * 60 : n);
  const valid = Number.isFinite(n) && n > 0 && seconds >= 3 && seconds <= 1800;
  const minForModel = model === 'D1_K' ? 5 : 3;
  const changed = enabled !== enabled0 || (enabled && seconds !== secs0);
  const st = door.lock_id ? CONFIG_STATE[state.data ?? 'unknown'] : undefined;

  return (
    <section className="stack" aria-labelledby="auto-lock-head">
      <h3 id="auto-lock-head">Automatisch op slot</h3>
      <p className="muted small">
        Het slot gaat na openen vanzelf weer op slot. Zo blijft de deur nooit per ongeluk open.
      </p>
      <label className="row" style={{ gap: 8 }}>
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        <span>Automatisch op slot {enabled ? 'aan' : 'uit'}</span>
      </label>
      {enabled ? (
        <div className="row" style={{ gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Field label="Na" hint="Van 3 seconden tot 30 minuten." error={!valid && amount !== '' ? 'Kies een tijd tussen 3 seconden en 30 minuten.' : null}>
            {(p) => (
              <input {...p} className="input" inputMode="numeric" style={{ width: 90 }} value={amount} onChange={(e) => setAmount(e.target.value)} />
            )}
          </Field>
          <Field label="Eenheid">
            {(p) => (
              <select {...p} className="input" value={unit} onChange={(e) => setUnit(e.target.value as 's' | 'min')}>
                <option value="s">seconden</option>
                <option value="min">minuten</option>
              </select>
            )}
          </Field>
        </div>
      ) : (
        <p className="small" role="note">
          Let op: zonder automatisch op slot blijft de deur open tot iemand hem met de hand op slot draait.
        </p>
      )}
      {enabled && valid && seconds < minForModel && (
        <p className="muted small">Dit slot kan minimaal {minForModel} seconden; het wordt {minForModel} seconden.</p>
      )}
      <div className="row" style={{ justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <span className="muted small">
          Nu: {enabled0 ? `na ${autoLockText(secs0)}` : 'uit'}
          {st && (
            <>
              {' · '}
              <Badge tone={st.tone}>{st.text}</Badge>
            </>
          )}
          {!door.lock_id && ' · wordt op het slot gezet zodra er een slot aan deze deur hangt.'}
        </span>
        <Button
          size="sm"
          variant="primary"
          disabled={!changed || (enabled && !valid) || save.isPending}
          onClick={async () => {
            try {
              await save.mutateAsync({ enabled, seconds: enabled ? seconds : secs0 });
              void state.refetch();
              toast(enabled ? `Automatisch op slot na ${autoLockText(seconds)}` : 'Automatisch op slot uitgezet');
            } catch (err) {
              toast(errorText(err, 'Opslaan mislukt'), 'error');
            }
          }}
        >
          Opslaan
        </Button>
      </div>
    </section>
  );
}
